import { useState, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
	Box,
	Button,
	Grid,
	TextField,
	Typography,
	InputAdornment,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Toolbar,
	Paper,
	Tooltip,
	MenuItem,
	CircularProgress,
} from "@mui/material";
import PropTypes from "prop-types";
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import CreateIcon from "@mui/icons-material/Create";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DownloadSharpIcon from "@mui/icons-material/DownloadSharp";
import PlayArrowSharpIcon from "@mui/icons-material/PlayArrowSharp";
import LoginSharpIcon from "@mui/icons-material/LoginSharp";
import LogoutSharpIcon from "@mui/icons-material/LogoutSharp";
import AddIcon from "@mui/icons-material/Add";
import { obtenerDatos, auditPost, obtenerStoresConfig } from "../../api/config";
import { visuallyHidden } from "@mui/utils";
import { GlobalContext } from "../../GlobalContext";
import Swal from "sweetalert2";

function createData(id, action, date, hour, full_name, module, description) {
	// Convertir la hora de UTC a la hora local del navegador
	const [hours, minutes, seconds] = hour.split(":");
	const utcDate = new Date();
	utcDate.setUTCHours(hours, minutes, seconds);

	const localTime = utcDate.toLocaleTimeString("en-GB", { hour12: false }); // Formato HH:mm:ss en 24 horas

	return {
		id,
		action,
		date,
		hour: localTime,
		full_name,
		module,
		description,
	};
}

const actionIcons = {
	read: <InfoIcon variant="contained" color="info" />,
	write: <AddIcon variant="contained" color="info" />,
	update: <CreateIcon variant="contained" color="warning" />,
	delete: <DeleteForeverIcon variant="contained" color="error" />,
	download: <DownloadSharpIcon variant="contained" color="info" />,
	execute: <PlayArrowSharpIcon variant="contained" color="success" />,
	login: <LoginSharpIcon variant="contained" color="info" />,
	logout: <LogoutSharpIcon variant="contained" color="info" />,
};

const getFilterOptions = (t, actionIcons) => [
	{ value: "read", label: t("audit.filter_options.read"), icon: actionIcons["read"] },
	{ value: "write", label: t("audit.filter_options.create"), icon: actionIcons["write"] },
	{ value: "update", label: t("audit.filter_options.update"), icon: actionIcons["update"] },
	{ value: "delete", label: t("audit.filter_options.delete"), icon: actionIcons["delete"] },
	{ value: "download", label: t("audit.filter_options.download"), icon: actionIcons["download"] },
	{ value: "execute", label: t("audit.filter_options.execute"), icon: actionIcons["execute"] },
	{ value: "login", label: t("audit.filter_options.login"), icon: actionIcons["login"] },
	{ value: "logout", label: t("audit.filter_options.logout"), icon: actionIcons["logout"] },
];

const getHeadCells = (t) => [
	{ id: "action", numeric: false, disablePadding: true, label: t("audit.head_cells.action") },
	{ id: "date", numeric: true, disablePadding: false, label: t("audit.head_cells.date") },
	{ id: "hour", numeric: true, disablePadding: false, label: t("audit.head_cells.hour") },
	{ id: "full_name", numeric: false, disablePadding: false, label: t("audit.head_cells.full_name") },

	{ id: "module", numeric: false, disablePadding: false, label: t("audit.head_cells.module") },
	{
		id: "description",
		numeric: false,
		disablePadding: false,
		label: t("audit.head_cells.description"),
	},
];

function descendingComparator(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}
function getComparator(order, orderBy) {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

const EnhancedTableHead = ({ headCells, order, orderBy, onRequestSort }) => {
	const createSortHandler = (property) => (event) => {
		onRequestSort(event, property);
	};

	return (
		<TableHead>
			<TableRow>
				<TableCell></TableCell>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align="left"
						padding={headCell.disablePadding ? "none" : "normal"}
						sortDirection={orderBy === headCell.id ? order : false}
					>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : "asc"}
							onClick={createSortHandler(headCell.id)}
						>
							{headCell.label}
							{orderBy === headCell.id ? (
								<Box component="span" sx={visuallyHidden}>
									{order === "desc" ? "sorted descending" : "sorted ascending"}
								</Box>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
};

EnhancedTableHead.propTypes = {
	onRequestSort: PropTypes.func.isRequired,
	order: PropTypes.oneOf(["asc", "desc"]).isRequired,
	orderBy: PropTypes.string.isRequired,
};

function EnhancedTableToolbar({
	searchTerm,
	handleSearchChange,
	filterValue,
	setFilterValue,
	filterOptions,
}) {
	const { t } = useTranslation();
	const handleFilterChange = (event) => {
		setFilterValue(event.target.value);
	};

	return (
		<Toolbar
			sx={{
				pl: { sm: 2 },
				pr: { xs: 1, sm: 1 },
			}}
		>
			<Typography sx={{ flex: "1 1 100%" }} variant="h6" id="tableTitle" component="div">
				{t("audit.title_grid")}
			</Typography>
			<Tooltip title="Buscar">
				<TextField
					sx={{ width: "40%", borderColor: "secondary.main", m: 1 }}
					size="small"
					label={t("aux.search")}
					value={searchTerm}
					onChange={handleSearchChange}
					type="search"
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton>
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Tooltip>

			<TextField
				sx={{
					width: "20%",
					borderColor: "secondary.main",
					m: 1,
				}}
				select
				label={t("audit.head_cells.action")}
				size="small"
				value={filterValue}
				onChange={handleFilterChange}
			>
				<MenuItem value="">{t("aux.all")}</MenuItem>
				{filterOptions.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.icon} {option.label}
					</MenuItem>
				))}
			</TextField>
		</Toolbar>
	);
}

EnhancedTableToolbar.propTypes = {
	searchTerm: PropTypes.string.isRequired,
	handleSearchChange: PropTypes.func.isRequired,
	filterValue: PropTypes.string.isRequired,
	setFilterValue: PropTypes.func.isRequired,
	filterOptions: PropTypes.array.isRequired,
};

const AuditModule = () => {
	const { t } = useTranslation();
	const headCells = useMemo(() => getHeadCells(t), [t]);
	const filterOptions = useMemo(() => getFilterOptions(t, actionIcons), [t]);
	const [fechaFin, setFechaFin] = useState("");
	const [fechaInicio, setFechaInicio] = useState("");
	const [fechaError, setFechaError] = useState("");
	const [order, setOrder] = useState("asc");
	const [orderBy, setOrderBy] = useState("name");
	const [searchTerm, setSearchTerm] = useState("");
	const [dataLoaded, setDataLoaded] = useState(false);
	const [rows, setRows] = useState([]);
	const [filterValue, setFilterValue] = useState("");
	const { site_id, getModuleId, isGranted } = useContext(GlobalContext);

	// Un único estado para manejar la descarga de Excel
	const [isDownloading, setIsDownloading] = useState(false);

	// Nuevo estado para manejar el "spinner" de carga al filtrar
	const [isLoading, setIsLoading] = useState(false);

	const [fechaInicioFilter, setFechaInicioFilter] = useState("");
	const [fechaFinFilter, setFechaFinFilter] = useState("");

	const handleDownload = async () => {
		setIsDownloading(true);
		try {
			// Construimos el payload para la descarga (con o sin filtro de acción)
			let data = construirData({
				start_date: fechaInicioFilter,
				end_date: fechaFinFilter,
			});
			if (filterValue) {
				data = {
					...data,
					action: capitalizeFirstLetter(filterValue),
				};
			}

			const tabla = await obtenerStoresConfig(`/files/get_auditoria?site_id=${site_id}`, data);

			const url = window.URL.createObjectURL(new Blob([tabla]), {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `${t("audit.title")}.xlsx`);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);

			// Auditamos según sea tabla filtrada o no
			const moduleId = filterValue
				? getModuleId("Auditoria", "Descarga tabla auditoria filtrada")
				: getModuleId("Auditoria", "Descarga tabla auditoria");

			auditPost(moduleId, site_id);

			Swal.fire({
				icon: "success",
				title: t("aux.messages.alert.success"),
				text: t("aux.messages.alert.text_alert.download_success"),
			});
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: t("aux.messages.alert.error"),
				text: t("aux.messages.alert.text_alert.error_download"),
			});
			console.error("Error al obtener datos:", error);
		} finally {
			setIsDownloading(false);
		}
	};

	const handleButtonClick = async () => {
		// Validamos fechas
		if (!fechaInicio || !fechaFin) {
			setFechaError(t("aux.messages.date_obligatory"));
			return;
		} else if (fechaInicio > fechaFin) {
			setFechaError(t("aux.messages.date_error"));
			return;
		} else {
			setFechaError("");
			setFechaInicioFilter(fechaInicio);
			setFechaFinFilter(fechaFin);
		}

		// Ponemos el spinner de carga
		setIsLoading(true);

		try {
			// llamada a la API
			const respuesta = await obtenerDatos(
				`/auditoria/get?start_date=${fechaInicio}&end_date=${fechaFin}&site_id=${site_id}`
			);
			const moduleId = getModuleId("Auditoria", "Ver el módulo de auditoria");
			auditPost(moduleId, site_id);

			// Si no hay datos
			if (
				respuesta === "No data found for the given period" ||
				respuesta === null ||
				respuesta === undefined ||
				respuesta === "" ||
				!respuesta
			) {
				setRows([]);
				setDataLoaded(true);
				return;
			}

			// Si hay datos, cargamos las filas
			const newRows = respuesta.map((item, index) =>
				createData(
					index + 1,
					item.action,
					item.date,
					item.hour,
					item.full_name,
					item.module,
					item.description
				)
			);

			setRows(newRows);
			setDataLoaded(true);
		} catch (error) {
			console.error("Error al filtrar auditoría:", error);
			setRows([]);
			setDataLoaded(true);
		} finally {
			// Quitamos el spinner al terminar
			setIsLoading(false);
		}
	};

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	const visibleRows = useMemo(() => {
		let filteredRows = rows;

		// Filtro por término de búsqueda (local)
		if (searchTerm) {
			filteredRows = filteredRows.filter((row) =>
				Object.keys(row).some((key) =>
					row[key].toString().toLowerCase().includes(searchTerm.toLowerCase())
				)
			);
		}

		// Filtro por acción seleccionada (local)
		if (filterValue) {
			filteredRows = filteredRows.filter((row) => row.action.toLowerCase() === filterValue);
		}

		return stableSort(filteredRows, getComparator(order, orderBy));
	}, [order, orderBy, rows, searchTerm, filterValue]);

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const construirData = (datos) => {
		// Filtramos solo las propiedades con valores válidos
		return Object.fromEntries(
			Object.entries(datos).filter(
				([, value]) => value !== "" && value !== null && value !== undefined
			)
		);
	};

	const capitalizeFirstLetter = (string) => {
		return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
	};

	return (
		<Box
			sx={{
				width: "100%",
				p: 2,
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<Typography variant="h5" align="center">
				{t("audit.title")}
			</Typography>
			<Box
				sx={{
					p: 2,
					width: "75%",
				}}
			>
				<Grid container spacing={2}>
					<Grid item xs={4}>
						<TextField
							id="dateInicio"
							label={t("aux.date_start")}
							required
							type="date"
							InputLabelProps={{
								shrink: true,
							}}
							style={{ width: "100%" }}
							value={fechaInicio}
							error={!!fechaError}
							helperText={fechaError && !fechaInicio ? fechaError : ""}
							onChange={(e) => setFechaInicio(e.target.value)}
						/>
					</Grid>
					<Grid item xs={4}>
						<TextField
							id="dateFin"
							label={t("aux.date_end")}
							required
							type="date"
							InputLabelProps={{
								shrink: true,
							}}
							style={{ width: "100%" }}
							value={fechaFin}
							error={!!fechaError}
							onChange={(e) => setFechaFin(e.target.value)}
							helperText={
								fechaError ? fechaError : fechaInicio > fechaFin ? t("aux.messages.date_error") : ""
							}
						/>
					</Grid>
					<Grid item xs={2}>
						<Button
							variant="outlined"
							sx={{ width: "100%", p: 1.7 }}
							size="large"
							onClick={handleDownload}
							color="primary"
							disabled={isDownloading || !isGranted("Auditoria", "Descarga tabla auditoria")}
						>
							{isDownloading ? t("aux.downloading") : t("aux.buttons.download")}
						</Button>
					</Grid>
					<Grid item xs={2}>
						<Button
							variant="contained"
							sx={{ width: "100%", p: 1.7 }}
							size="large"
							onClick={handleButtonClick}
						>
							{t("aux.buttons.filter")}
						</Button>
					</Grid>
				</Grid>
				<Box
					sx={{
						width: "100%",
						pt: 2,
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					{isLoading ? (
						/* Si está cargando, mostramos el spinner */
						<Box textAlign="center" sx={{ mt: 2 }}>
							<CircularProgress />
							<Typography variant="body2" sx={{ mt: 1 }}>
								{t("aux.loading")}
							</Typography>
						</Box>
					) : dataLoaded ? (
						rows.length === 0 ? (
							<Typography variant="body1">{t("aux.no_data")}</Typography>
						) : (
							<Paper
								variant="outlined"
								elevation={0}
								sx={{
									width: "100%",
									maxHeight: "60vh",
									overflow: "auto",
								}}
							>
								<EnhancedTableToolbar
									filterOptions={filterOptions}
									searchTerm={searchTerm}
									handleSearchChange={handleSearchChange}
									filterValue={filterValue}
									setFilterValue={setFilterValue}
								/>
								<TableContainer>
									<Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
										<EnhancedTableHead
											headCells={headCells}
											order={order}
											orderBy={orderBy}
											onRequestSort={handleRequestSort}
										/>
										<TableBody>
											{visibleRows.map((row, index) => {
												const labelId = `enhanced-table-checkbox-${index}`;
												return (
													<TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
														<TableCell component="th" id={labelId}>
															{actionIcons[row.action.toLowerCase()]}
														</TableCell>
														<TableCell component="th" id={labelId} scope="row" padding="none">
															{row.action}
														</TableCell>
														<TableCell align="left">{row.date}</TableCell>
														<TableCell align="left">{row.hour}</TableCell>
														<TableCell align="left">{row.full_name}</TableCell>

														<TableCell align="left">{row.module}</TableCell>
														<TableCell align="left">{row.description}</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							</Paper>
						)
					) : null}
				</Box>
			</Box>
		</Box>
	);
};

export { AuditModule };
